// This file configures the initialization of Sentry on the client.
// The config added here will be used whenever a page is loaded.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/editor/:id\",\"regex\":\"^/editor/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id\",\"regex\":\"^/projects/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/analytics\",\"regex\":\"^/projects/([^/]+)/analytics$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/books\",\"regex\":\"^/projects/([^/]+)/books$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/books/:bookId\",\"regex\":\"^/projects/([^/]+)/books/([^/]+)$\",\"paramNames\":[\"id\",\"bookId\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/books/:bookId/settings\",\"regex\":\"^/projects/([^/]+)/books/([^/]+)/settings$\",\"paramNames\":[\"id\",\"bookId\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/timeline\",\"regex\":\"^/projects/([^/]+)/timeline$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/admin\"},{\"path\":\"/admin/audit-logs\"},{\"path\":\"/admin/editor-options\"},{\"path\":\"/admin/invites\"},{\"path\":\"/admin/projects\"},{\"path\":\"/admin/settings\"},{\"path\":\"/admin/templates\"},{\"path\":\"/admin/users\"},{\"path\":\"/auth/signin\"},{\"path\":\"/auth/signup\"},{\"path\":\"/auth/verify-email\"},{\"path\":\"/profile\"},{\"path\":\"/settings\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.2.9";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import * as Sentry from '@sentry/nextjs';

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

  // Environment
  environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || process.env.NODE_ENV,

  // Release tracking - Critical for source map association
  release: process.env.NEXT_PUBLIC_APP_VERSION,

  // Client-side sampling rates
  tracesSampleRate: parseFloat(process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
  sampleRate: parseFloat(process.env.NEXT_PUBLIC_SENTRY_SAMPLE_RATE || '1.0'),

  // Enable/Disable
  enabled: process.env.NEXT_PUBLIC_SENTRY_ENABLED !== 'false',

  // Debug mode
  debug: process.env.NEXT_PUBLIC_SENTRY_DEBUG === 'true',

  // Source map configuration
  // Sentry.init automatically handles source map retrieval from:
  // 1. Next.js .next/static/chunks/.map files (during build)
  // 2. Sentry Release API (if uploaded via sentry-cli)
  // 3. JavaScript source map comment (inline or external)
  attachStacktrace: true,

  // Integrations
  integrations: [
    // Browser tracing for performance monitoring
    Sentry.browserTracingIntegration(),

    // Session Replay - captures user interactions during errors
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
      maskAllInputs: true,
    }),

    // React Router integration (if using React Router)
    // Sentry.reactRouterV6BrowserTracingIntegration({
    //   useEffect: React.useEffect,
    // }),
  ],

  // Session Replay configuration
  replaysSessionSampleRate: parseFloat(process.env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1'),
  replaysOnErrorSampleRate: parseFloat(process.env.NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'),

  // Breadcrumbs
  beforeBreadcrumb(breadcrumb) {
    // Filter out noisy breadcrumbs
    if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
      return null;  // Don't capture console.log
    }
    return breadcrumb;
  },

  // Data scrubbing (before sending to Sentry)
  beforeSend(event) {
    // Remove sensitive data from client events
    if (event.request?.cookies) {
      delete event.request.cookies;
    }

    if (event.request?.headers) {
      delete event.request.headers['authorization'];
      delete event.request.headers['cookie'];
      delete event.request.headers['x-api-key'];
    }

    // Remove sensitive query params
    if (event.request?.url) {
      try {
        const url = new URL(event.request.url);
        url.searchParams.delete('token');
        url.searchParams.delete('api_key');
        url.searchParams.delete('password');
        event.request.url = url.toString();
      } catch {
        // Silently ignore if URL parsing fails
      }
    }

    // Remove sensitive form data
    if (event.request?.data) {
      const data = event.request.data as any;
      if (data.password) data.password = '[Filtered]';
      if (data.token) data.token = '[Filtered]';
      if (data.api_key) data.api_key = '[Filtered]';
      if (data.apiKey) data.apiKey = '[Filtered]';
    }

    return event;
  },

  // Ignore specific errors
  ignoreErrors: [
    // Browser extensions and content scripts
    'top.GLOBALS',
    'chrome-extension://',
    'firefox-extension://',
    'moz-extension://',

    // Network errors (often user connection issues)
    'NetworkError',
    'Failed to fetch',
    'fetch is not defined',
    'XMLHttpRequest',
    'Load failed',

    // Next.js hot reload errors (development)
    'NEXT_NOT_FOUND',
    'NEXT_REDIRECT',

    // Third-party scripts
    'script error',
    'Script error.',

    // Browser console errors
    'ResizeObserver loop',
    'ResizeObserver loop limit exceeded',
    'Non-Error promise rejection',

    // Tiptap editor errors (often user-triggered)
    'Schema does not support mark type',
  ],

  // Performance monitoring
  tracesSampler(samplingContext) {
    // Never sample health checks or internal routes
    if (samplingContext.request?.url) {
      const url = samplingContext.request.url;

      // Skip Next.js internal routes
      if (url.includes('/_next/')) {
        return 0;
      }

      // Skip health/status checks
      if (url.includes('/api/health') || url.includes('/api/status')) {
        return 0;
      }
    }

    // Use configured sample rate for everything else
    return parseFloat(process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE || '0.1');
  },

  // Tunneling (optional): Route errors through your own domain to bypass ad-blockers
  // tunnel: '/monitoring',
});

// Export router transition tracking for App Router
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;